import React from "react";
import { Box, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import LabeledSelect from "../../../Widgets/LabeledSelect";
import LabeledTextArea from "../../../Widgets/LabeledTextArea";
import {
  TelehealthPrerequisitesFormValues,
  DROPDOWN_FIELDS,
  MINUTE_PAIR_FIELDS,
  TEXT_AREA_FIELDS,
  YES_NO_SOMETIMES_OPTIONS,
} from "./interface";

interface EditFieldsProps {
  formValues: TelehealthPrerequisitesFormValues;
  handleChange: (
    field: keyof TelehealthPrerequisitesFormValues
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleToggleNA: (
    field: keyof TelehealthPrerequisitesFormValues
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const EditFields: React.FC<EditFieldsProps> = ({
  formValues,
  handleChange,
  handleToggleNA,
}) => {
  return (
    <Grid container spacing={7}>
      {DROPDOWN_FIELDS.map(({ label, key }) => (
        <LabeledSelect
          key={key}
          label={label}
          value={formValues[key]}
          onChange={handleChange(key)}
          options={YES_NO_SOMETIMES_OPTIONS}
          placeholder="Please select"
        />
      ))}

      {MINUTE_PAIR_FIELDS.map(({ label, minutesKey, totalKey, naKey, minutesHelper, totalHelper }) => {
        const isNotApplicable = Boolean(formValues[naKey]);
        return (
          <Grid item xs={12} key={minutesKey}>
            <Stack gap={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography variant="inputLabel" sx={{ color: "text.secondary" }}>
                  {label}
                </Typography>
                <FormControlLabel
                  sx={{ flexShrink: 0, mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isNotApplicable}
                      onChange={handleToggleNA(naKey)}
                    />
                  }
                  label="Not Applicable"
                />
              </Box>
              <Stack gap={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={formValues[minutesKey]}
                    onChange={handleChange(minutesKey)}
                    inputProps={{ min: 0 }}
                    fullWidth
                    disabled={isNotApplicable}
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body1" sx={{ flexShrink: 0 }}>
                    of
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={formValues[totalKey]}
                    onChange={handleChange(totalKey)}
                    inputProps={{ min: 0 }}
                    fullWidth
                    disabled={isNotApplicable}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 5 }}>
                  <Typography variant="caption" sx={{ flex: 1, color: "text.secondary" }}>
                    {minutesHelper}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ flexShrink: 0, visibility: "hidden" }}
                    aria-hidden="true"
                  >
                    of
                  </Typography>
                  <Typography variant="caption" sx={{ flex: 1, color: "text.secondary" }}>
                    {totalHelper}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Grid>
        );
      })}

      {TEXT_AREA_FIELDS.map(({ label, key, placeholder }) => (
        <LabeledTextArea
          key={key}
          label={label}
          value={formValues[key]}
          onChange={handleChange(key)}
          multiline
          minRows={3}
          placeholder={placeholder}
        />
      ))}
    </Grid>
  );
};

export default EditFields;
